import { costCenterSplitIssue, isValidCpf } from '@voll-intelligence/domain';
import type { CheckoutField, CheckoutTravelerDraft } from '../product_checkout_schema.js';

/** One allocation row as the screen holds it: ids, and the percent of a split. */
export interface CanSubmitAllocation {
  companyId: string;
  costCenterId: string;
  projectId: string;
  percent?: number | undefined;
}

export interface CanSubmitInput {
  fields: CheckoutField[];
  withinPolicy: boolean;
  enableProjectField: boolean;
  /** The traveler's allocation rows: one, or a split of up to 10 (#6697). */
  allocations: readonly CanSubmitAllocation[];
  /** The `allow_many_allocations` flag of whoever books: without it, more than
   *  one row blocks. Default `false`. */
  allowManyAllocations?: boolean;
  motiveId: string;
  justificationId: string | undefined;
  /** Server-flagged missing traveler fields (`CorporateBookingForm.traveler.missingRequired`). */
  missingRequired: Array<'birthdate' | 'nationality' | 'gender' | 'document' | 'embark'>;
  travelerDraft: CheckoutTravelerDraft;
  /** `false` → this block does not own the rateio (multi de hotel: só o
   *  requester aloca; o BFF descarta as allocations dos acompanhantes).
   *  Default `true` (voo: todo bloco aloca). */
  allocationRequired?: boolean;
  /** `false` → the company's justification catalog is empty, so the screen
   *  has no select to render and the justification cannot gate the submit
   *  (the dead end of #5514). Default `true` (BFF validation, no catalog). */
  justificationSelectable?: boolean;
}

function hasField(fields: CheckoutField[], kind: CheckoutField['kind']): boolean {
  return fields.some((f) => f.kind === kind);
}

export function hasSelectField(
  fields: CheckoutField[],
  id: Extract<CheckoutField, { kind: 'select' }>['id'],
): boolean {
  return fields.some((field) => field.kind === 'select' && field.id === id);
}

/** Company/costCenter always required on every row when the manifest has an
 *  `allocation` field; `project` only when the client's `enableProjectField` is
 *  on. The project is its own blocker: with company and cost center filled,
 *  naming "company and cost center" sent the person to the wrong field (#3841).
 *  A split that breaks `costCenterSplitIssue` (sum, percent, repeated cost
 *  center, rows over the flag) blocks as `allocation`. */
function allocationBlockers(input: CanSubmitInput): Array<'allocation' | 'project'> {
  if (input.allocationRequired === false) return [];
  if (!hasField(input.fields, 'allocation')) return [];
  const rows = input.allocations;
  if (rows.length === 0 || rows.some((row) => !row.companyId || !row.costCenterId)) {
    return ['allocation'];
  }
  const issue = costCenterSplitIssue(rows, {
    allowManyRows: input.allowManyAllocations === true,
  });
  if (issue) return ['allocation'];
  return !input.enableProjectField || rows.every((row) => row.projectId) ? [] : ['project'];
}

/** Justification only when out of policy AND the screen actually renders the
 *  `justification` select: the manifest carries it and the catalog has
 *  something to choose. Motive is always required. */
function justificationOk(input: CanSubmitInput): boolean {
  const needsJustification =
    !input.withinPolicy &&
    input.justificationSelectable !== false &&
    hasSelectField(input.fields, 'justification');
  return !needsJustification || Boolean(input.justificationId);
}

type MissingRequired = CanSubmitInput['missingRequired'][number];

/** Uma pendência que trava o submit — o que a tela nomeia junto ao CTA. */
export type SubmitBlocker =
  | MissingRequired
  | 'phone'
  | 'allocation'
  | 'project'
  | 'motive'
  | 'justification'
  | 'travel_docs';

const TRAVELER_FIELD_VALIDATORS: Record<
  MissingRequired,
  (draft: CheckoutTravelerDraft) => boolean
> = {
  birthdate: (draft) => Boolean(draft.birthdate),
  nationality: (draft) => Boolean(draft.nationality?.code),
  gender: (draft) => Boolean(draft.gender),
  document: (draft) => {
    const document = draft.document;
    if (!document?.value.trim()) return false;
    // `document` só falta quando o perfil não tem NENHUM documento em ficha, então
    // o CPF aqui é digitado e vai no fio (#5809). Só passa com o dígito
    // verificador certo: o BFF recusa o resto (`checkout_validation.ts`).
    if (document.type === 'cpf') return isValidCpf(document.value.replace(/\D/g, ''));
    return document.type !== 'passport' || Boolean(document.issuingCountry);
  },
  embark: (draft) => Boolean(draft.boardingName?.trim() && draft.boardingSurname?.trim()),
};

/** CPF digitado e inválido trava mesmo com outro documento em ficha (`document`
 *  fora de `missingRequired`): o mapper o descartaria calado (`typedCpf`), e nem
 *  a reserva nem o perfil receberiam o que o viajante digitou. Vazio não trava. */
function invalidTypedCpf(draft: CheckoutTravelerDraft): boolean {
  const document = draft.document;
  if (document?.type !== 'cpf' || !document.value.trim()) return false;
  return !isValidCpf(document.value.replace(/\D/g, ''));
}

/** Traveler blockers in manifest (= screen) order. Birthdate/nationality/
 *  document/gender/embark are gated only when the manifest carries that field
 *  kind AND the server flags it missing on the traveler profile; `phone` is
 *  gated by the manifest alone (it never enters `missingRequired`). */
function travelerBlockers(input: CanSubmitInput): SubmitBlocker[] {
  const { fields, missingRequired, travelerDraft } = input;
  return fields.flatMap((field): SubmitBlocker[] => {
    if (field.kind === 'phone') return travelerDraft.phone?.trim() ? [] : ['phone'];
    const kind = missingRequired.find((missing) => missing === field.kind);
    if (kind) return TRAVELER_FIELD_VALIDATORS[kind](travelerDraft) ? [] : [kind];
    return field.kind === 'document' && invalidTypedCpf(travelerDraft) ? ['document'] : [];
  });
}

/**
 * What still blocks the submit, in screen order (identity → allocation →
 * motive → justification). Requiredness sources: the field manifest (which
 * fields exist at all) + server flags (`missingRequired`) + each field's
 * intrinsic rule (e.g. passport ⇒ issuingCountry). Budget is NEVER an input
 * here (design spec §Budget) — every product submits and the server decides.
 * O drawer nomeia esta lista junto ao CTA: um botão cinza sem motivo era
 * beco sem saída (#4206).
 */
export function computeSubmitBlockers(input: CanSubmitInput): SubmitBlocker[] {
  return [
    ...travelerBlockers(input),
    ...allocationBlockers(input),
    ...(input.motiveId ? [] : (['motive'] as const)),
    ...(justificationOk(input) ? [] : (['justification'] as const)),
  ];
}

/** Single `canSubmit`, closing the rental gap where company/costCenter/project
 *  were never validated — nothing left in `computeSubmitBlockers`. */
export function computeCanSubmit(input: CanSubmitInput): boolean {
  return computeSubmitBlockers(input).length === 0;
}
